import React, { useState, useEffect, useRef } from 'react';
import Modal from '../Modal/Modal';
import { toast } from 'sonner';
import { REGISTRATION_STEPS } from '../../constants';
import type { ParentRegistrationFormData } from '../../types';
import type { CreateCaseRequest } from '../../types/api';
import {
  parseParentStep,
  parentRegistrationSchema,
} from '../../schemas/parentRegistrationSchemas';
import { ZodError } from 'zod';
import { createCase } from '../../api/services/casesApi';
import { getErrorMessage } from '../../api/errorHandler';
import { useAuthContext } from '../../contexts/useAuthContext';
import { useModal } from '../../contexts/useModal';
import { ArrowRight } from 'lucide-react';
import {
  savePendingRegistration,
  getPendingRegistration,
  clearPendingRegistration,
} from '../../utils/storage';
import TuitionDetailsStep from '../ParentCaseForm/TuitionDetailsStep';
import SubjectDurationStep from '../ParentCaseForm/SubjectDurationStep';
import LocationTimeStep from '../ParentCaseForm/LocationTimeStep';
import BudgetStep from '../ParentCaseForm/BudgetStep';
import { captureError, pushBreadcrumb } from '@/debug/debugStore';
import { useOptionalI18n } from '@/i18n';
import { localizeTemplate, localizeText } from '@/i18n/options';
import { logger } from '@/utils/logger';

interface ParentRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ParentRegistrationModal: React.FC<ParentRegistrationModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { backendUser, profile, updateProfile } = useAuthContext();
  const { openLoginModal, openCloseWarning } = useModal();
  const { locale } = useOptionalI18n();
  const [currentStep, setCurrentStep] = useState(0);

  const [formData, setFormData] = useState<ParentRegistrationFormData>({
    // Step 0: Tuition Details
    tuitionType: '',
    educationSystem: '',
    level: '',

    // Step 1: Subject & Duration
    subject: '',
    teachingLanguages: [],
    lessonDuration: 0,
    lessonsPerWeek: 0,
    tutorRemarks: '',

    // Step 2: Location & Time
    state: '',
    district: '',
    areaNote: '',
    preferredTimes: {},

    // Step 3: Budget
    hourlyRate: 50,
    termsAccepted: false,
  });

  // Validation errors state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const errorsRef = useRef<Record<string, string>>({});
  const stepScrollRef = useRef<HTMLDivElement>(null);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);

  // Keep ref in sync with errors state
  useEffect(() => {
    errorsRef.current = errors;
  }, [errors]);

  useEffect(() => {
    const el = stepScrollRef.current;
    if (!el) return;
    const check = () => {
      setShowLeftGradient(el.scrollLeft > 0);
      setShowRightGradient(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
    };
    check();
    el.addEventListener('scroll', check, { passive: true });
    return () => el.removeEventListener('scroll', check);
  }, [isOpen]);

  // Helper to extract field errors from ZodError
  const extractParentFieldErrors = (zodError: ZodError): Record<string, string> => {
    const fieldErrors: Record<string, string> = {};
    zodError.issues.forEach((issue) => {
      const field = issue.path[0] as string;
      if (field && !fieldErrors[field]) {
        fieldErrors[field] = issue.message;
      }
    });
    return fieldErrors;
  };

  const parseCurrentStep = (step: number) => parseParentStep(step, formData);

  // Real-time error clearing: clear errors for fields that now pass validation
  useEffect(() => {
    const currentErrors = errorsRef.current;
    if (Object.keys(currentErrors).length === 0) return;

    const result = parseCurrentStep(currentStep);

    if (result.success) {
      setErrors({});
      return;
    }

    const stillFailingFields = extractParentFieldErrors(result.error as ZodError);
    const updatedErrors: Record<string, string> = {};

    for (const fieldName of Object.keys(currentErrors)) {
      if (stillFailingFields[fieldName]) {
        updatedErrors[fieldName] = stillFailingFields[fieldName];
      }
    }

    if (Object.keys(updatedErrors).length !== Object.keys(currentErrors).length) {
      setErrors(updatedErrors);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, currentStep]);

  // Submission loading state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const activeRole = profile?.role ?? backendUser?.currentRole ?? null;
  const hasRoleMismatch = Boolean(backendUser && activeRole !== 'parent');

  const validateCurrentStep = (): boolean => {
    setErrors({});
    const result = parseParentStep(currentStep, formData);
    if (result.success) return true;
    if ('error' in result) {
      setErrors(extractParentFieldErrors(result.error as ZodError));
    }
    return false;
  };

  const nextStep = () => {
    const formDataSnapshot = structuredClone(formData);
    logger.log('ParentRegistration', `Next click snapshot at step ${currentStep + 1}`, formDataSnapshot, true);

    if (validateCurrentStep() && currentStep < REGISTRATION_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
      pushBreadcrumb({ category: 'user', message: `Parent reg: step ${currentStep + 1} -> ${currentStep + 2}` });
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setErrors({}); // Clear errors when going back
      setCurrentStep(currentStep - 1);
      pushBreadcrumb({ category: 'user', message: `Parent reg: step ${currentStep + 1} -> ${currentStep}` });
    }
  };

  const handleSafeClose = () => {
    const isDirty = formData.tuitionType !== '' || formData.subject !== '' || formData.state !== '' || currentStep > 0;
    if (isDirty && !isSubmitting) {
      openCloseWarning({
        title: localizeText(locale, 'Unsaved Changes'),
        message: localizeText(locale, 'You have unsaved changes in your form. Are you sure you want to discard them?'),
        cancelText: localizeText(locale, 'Continue Editing'),
        confirmText: localizeText(locale, 'Discard'),
        onConfirm: onClose,
      });
      return;
    }
    // We clear errors but don't reset form so if they re-open they might keep state? Or maybe they don't.
    setErrors({});
    onClose();
  };

  // Restore pending form data when modal opens so user can review and submit
  useEffect(() => {
    if (!isOpen) return;
    const pending = getPendingRegistration();
    if (pending?.type === 'parent') {
      const pendingFormData = pending.formData as ParentRegistrationFormData;
      setFormData(pendingFormData);
      setCurrentStep(REGISTRATION_STEPS.length - 1);
      clearPendingRegistration();
    }
  }, [isOpen]);

  // Register with DevPanel for debug controls
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!isOpen) {
      window.__devPanel?.unregister('parent');
      return;
    }
    window.__devPanel?.register({
      type: 'parent',
      setFormData: (data) => setFormData(data as ParentRegistrationFormData),
      setCurrentStep: setCurrentStep,
      stepCount: REGISTRATION_STEPS.length,
      get currentStep() { return currentStep; },
    });
    return () => {
      window.__devPanel?.unregister('parent');
    };
  }, [isOpen, currentStep]);

  // Register current form state with debug bridge (production-safe)
  useEffect(() => {
    if (!isOpen) {
      window.__debugBridge?.unregisterFormData('parent');
      return;
    }
    window.__debugBridge?.registerFormData('parent', () => ({
      formData: formData as unknown as Record<string, unknown>,
      validationErrors: errorsRef.current,
      currentStep,
      stepCount: REGISTRATION_STEPS.length,
    }));
    return () => {
      window.__debugBridge?.unregisterFormData('parent');
    };
  }, [isOpen, currentStep, formData]);

  const handleSubmit = async () => {
    if (hasRoleMismatch) {
      toast.error(localizeText(locale, 'You are currently logged in as a tutor. Please switch to a parent account before submitting a tuition case.'));
      return;
    }

    if (!formData.termsAccepted) {
      toast.error(localizeText(locale, 'Please accept the Terms of Service and Privacy Policy to proceed'));
      return;
    }

    // Check if user is authenticated; if not, save form and open login modal
    if (!backendUser) {
      savePendingRegistration('parent', formData as unknown as Record<string, unknown>);
      onClose();
      openLoginModal({ fromRegistration: true });
      return;
    }

    // Validate all steps before submitting
    try {
      parentRegistrationSchema.parse(formData);

      setIsSubmitting(true);
      setErrors({});
      pushBreadcrumb({ category: 'user', message: 'Parent reg: submit initiated' });

      const payload = {
        tuitionType: formData.tuitionType as 'physical' | 'online' | 'both',
        educationSystem: formData.educationSystem,
        level: formData.level as CreateCaseRequest['level'],
        subject: formData.subject,
        teachingLanguages: formData.teachingLanguages,
        lessonsPerWeek: formData.lessonsPerWeek,
        lessonDuration: formData.lessonDuration,
        tutorRemarks: formData.tutorRemarks || undefined,
        state: formData.tuitionType === 'online' ? undefined : formData.state,
        district: formData.tuitionType === 'online' ? undefined : (formData.district || undefined),
        areaNote: formData.tuitionType === 'online' ? undefined : (formData.areaNote || undefined),
        preferredTimes: Object.keys(formData.preferredTimes).length > 0 ? formData.preferredTimes : undefined,
        hourlyRate: formData.hourlyRate,
      };

      // Call API to create case
      await createCase(payload);

      // Success - show toast then close modal
      toast.success(localizeText(locale, 'Tuition case created successfully!'));

      // Update profile in context to ensure NavBar reflects latest state
      if (profile) {
        updateProfile(profile);
      }

      // Close modal after brief delay
      setTimeout(() => {
        onClose();

        // Reset form after modal is closed (invisible to user)
        setFormData({
          tuitionType: '',
          educationSystem: '',
          level: '',
          subject: '',
          teachingLanguages: [],
          lessonDuration: 0,
          lessonsPerWeek: 0,
          tutorRemarks: '',
          state: '',
          district: '',
          areaNote: '',
          preferredTimes: {},
          hourlyRate: 50,
          termsAccepted: false,
        });
        setErrors({});
        setCurrentStep(0);
      }, 1000);
    } catch (error) {
      if (error instanceof ZodError) {
        const newErrors: Record<string, string> = {};
        error.issues.forEach((err) => {
          const field = err.path[0] as string;
          newErrors[field] = err.message;
        });
        setErrors(newErrors);
        toast.error(localizeText(locale, 'Please complete all required fields correctly'));
      } else {
        // API error
        const errorMessage = getErrorMessage(error);
        captureError('ParentReg:createCase', error);
        toast.error(localizeTemplate(locale, 'Failed to create case: {value}', errorMessage));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleSafeClose}
      closeOnOverlayClick={false}
      title={localizeText(locale, 'Post a Tuition Case')}
      maxWidth="max-w-3xl"
      footer={
        <div className="w-full bg-gray-50/50 border-t border-gray-100">
          {currentStep === REGISTRATION_STEPS.length - 1 && hasRoleMismatch && (
            <div className="px-1 pb-3 text-sm font-medium text-red-600">
              {localizeText(locale, 'You are currently logged in as a tutor. Please switch to a parent account before submitting a tuition case.')}
            </div>
          )}
          <div className="flex justify-between items-center gap-4">
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              className={`font-medium text-sm transition-colors ${currentStep === 0
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              {localizeText(locale, 'Previous')}
            </button>
            {currentStep < REGISTRATION_STEPS.length - 1 ? (
              <button onClick={nextStep} className="btn-secondary flex items-center gap-2">
                {localizeText(locale, 'Next')}
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !formData.termsAccepted || hasRoleMismatch}
                className={`btn-secondary flex items-center gap-2 ${(isSubmitting || !formData.termsAccepted || hasRoleMismatch)
                    ? 'opacity-50 cursor-not-allowed'
                    : ''
                  }`}
              >
                {isSubmitting
                  ? localizeText(locale, 'Submitting...')
                  : localizeText(locale, 'Submit Case')}
                {!isSubmitting && <ArrowRight className="w-4 h-4" />}
              </button>
            )}
          </div>
        </div>
      }
    >
      {/* Step Progress Indicators */}
      <div className="relative mb-8">
        {showLeftGradient && (
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent z-10" />
        )}
        {showRightGradient && (
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent z-10" />
        )}
        <div ref={stepScrollRef} className="overflow-x-auto scrollbar-hide px-1 py-1 mb-2">
        <div className="flex items-center gap-2 min-w-max">
          {REGISTRATION_STEPS.map((_step, index) => (
            <div key={index} className={`flex items-center ${index < REGISTRATION_STEPS.length - 1 ? 'flex-1' : ''}`}>
              <div className="flex items-center flex-1">
                <button
                  className={`w-8 h-8 rounded-full font-semibold flex items-center justify-center transition-all flex-shrink-0 ${
                    index < currentStep
                      ? 'bg-secondary text-white'
                      : index === currentStep
                      ? 'bg-secondary text-white ring-2 ring-secondary ring-offset-2'
                      : 'bg-white border-2 border-gray-300 text-gray-400'
                  }`}
                  disabled
                >
                  {index < currentStep ? '✓' : index + 1}
                </button>
                {index < REGISTRATION_STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 transition-colors ${
                      index < currentStep ? 'bg-secondary' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
        </div>
        <div className="text-sm font-medium text-gray-600">
          {locale === 'zh-CN'
            ? `第 ${currentStep + 1} 步，共 ${REGISTRATION_STEPS.length} 步 — ${localizeText(locale, REGISTRATION_STEPS[currentStep])}`
            : `Step ${currentStep + 1} of ${REGISTRATION_STEPS.length} — ${REGISTRATION_STEPS[currentStep]}`}
        </div>
      </div>

      {/* Step Content */}
      <div className="min-h-[400px]">
        {currentStep === 0 && (
          <TuitionDetailsStep
            formData={formData}
            errors={errors}
            onChange={(data) => setFormData({ ...formData, ...data })}
            showBothOption={true}
          />
        )}

        {currentStep === 1 && (
          <SubjectDurationStep
            formData={formData}
            errors={errors}
            onChange={(data) => setFormData({ ...formData, ...data })}
          />
        )}

        {currentStep === 2 && (
          <LocationTimeStep
            formData={formData}
            errors={errors}
            onChange={(data) => setFormData({ ...formData, ...data })}
          />
        )}

        {currentStep === 3 && (
          <BudgetStep
            formData={formData}
            errors={errors}
            onChange={(data) => setFormData({ ...formData, ...data })}
            showTerms={true}
          />
        )}
      </div>

    </Modal>
  );
};

export default ParentRegistrationModal;
